/**
 * @fileoverview `TourHistoryRow` — single past-event row for the artist tour history list.
 *
 * Displays date · title · venue · city/state, with optional outbound tickets link.
 * Pure presentational; consumed by `ArtistTourHistory` (WEB-105).
 *
 * @since WEB-92
 */
import * as React from 'react';
import { ExternalLink } from 'lucide-react';
import type { ArtistCityEvent } from '@/types/artistDetail';
import { cn } from '@/components/atoms/primitives/utils';

export interface TourHistoryRowProps {
  event: ArtistCityEvent;
  className?: string;
}

const MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/**
 * Format an ISO date string as `MMM d, yyyy` in UTC. We format in UTC so the
 * rendered day matches the calendar day stored on the event regardless of the
 * viewer's local timezone — concert dates are inherently date-only data.
 */
function formatEventDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const month = MONTHS_SHORT[date.getUTCMonth()];
  const day = date.getUTCDate();
  const year = date.getUTCFullYear();
  return `${month} ${day}, ${year}`;
}

function formatLocationLine(location: ArtistCityEvent['location']): string | null {
  if (location == null) return null;
  if (location.city && location.state) return `${location.city}, ${location.state}`;
  if (location.city) return location.city;
  return null;
}

export function TourHistoryRow({ event, className }: TourHistoryRowProps): React.ReactElement {
  const formattedDate = formatEventDate(event.date);
  const locationLine = formatLocationLine(event.location);

  return (
    <article
      aria-label={event.title}
      className={cn(
        'grid grid-cols-[auto_1fr_auto] items-baseline gap-x-4 gap-y-1 py-3',
        'border-b border-border/40 last:border-b-0',
        className,
      )}
    >
      <time
        dateTime={event.date}
        className="text-sm font-medium tabular-nums text-muted-foreground whitespace-nowrap"
      >
        {formattedDate}
      </time>
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-foreground">{event.title}</p>
        {(event.location?.name || locationLine) && (
          <p className="truncate text-xs text-muted-foreground">
            {event.location?.name && <span>{event.location.name}</span>}
            {locationLine && (
              <>
                {event.location?.name && <span aria-hidden="true"> · </span>}
                <span>{locationLine}</span>
              </>
            )}
          </p>
        )}
      </div>
      {event.ticketUrl && (
        <a
          href={event.ticketUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Tickets for ${event.title} (opens in new tab)`}
          className="inline-flex items-center gap-1 rounded-sm py-2 px-3 text-xs font-medium text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          Tickets
          <ExternalLink className="h-3 w-3" aria-hidden="true" />
        </a>
      )}
    </article>
  );
}
